import { Component, computed, inject, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { provideIcons } from '@ng-icons/core';
import { lucideEye, lucideTrash } from '@ng-icons/lucide';
import { CheckboxSubmenuItemComponent } from '@shared/components/checkbox-submenu-item/checkbox-submenu-item.component';
import { ExpansionPanel, ExpansionPanelMenuAction } from '@shared/components/expansion-panel/expansion-panel.component';
import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { HlmAutocompleteImports } from '@spartan-ng/helm/autocomplete';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmIconImports } from '@spartan-ng/helm/icon';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmItemSeparator } from '@spartan-ng/helm/item';
import { HlmRadioGroupImports } from '@spartan-ng/helm/radio-group';
import { InterrelationStore } from '../../../../../référentiel-métier/référentiel-metier-interrelations/store/interrelation.store';
import { Aptitude } from '../../../../../référentiel-métier/référentiel-métier-processus/processus-aptitude/models/aptitude.model';
import { Production } from '../../../../../référentiel-métier/référentiel-métier-processus/processus-aptitude/models/production.model';
import { FormControlNames } from '../../../../models/form-control-names.model';
import { Étape } from '../../../../models/étape.model';
import { ModuleDetailFormService } from '../../module-detail-form.service';

@Component({
  selector: 'app-module-objectif-group',
  imports: [
    HlmInputImports,
    HlmFieldImports,
    ReactiveFormsModule,
    ExpansionPanel,
    CheckboxSubmenuItemComponent,
    HlmAutocompleteImports,
    HlmIconImports,
    HlmButtonImports,
    HlmItemSeparator,
    HlmRadioGroupImports,
    LabelRequiredDirective,
  ],
  templateUrl: './objectif-group.component.html',
  providers: [
    provideIcons({
      lucideTrash,
      lucideEye,
    }),
  ],
})
export class ModuleObjectifGroupComponent {
  readonly #interrelationStore = inject(InterrelationStore);
  readonly form = input.required<FormGroup>();
  readonly index = input.required<number | string>();
  readonly supprimerAptitude = output();
  readonly etape = input.required<Étape>();
  readonly detailFormService = inject(ModuleDetailFormService);
  readonly aptitudesCertification = computed(() => this.detailFormService.aptitudesCertif());

  readonly actionAptitude: ExpansionPanelMenuAction<Aptitude>[] = [
    {
      icon: 'lucideTrash',
      label: "Supprimer l'aptitude du module",
      action: () => this.supprimerAptitude.emit(),
      class: 'invisible group-hover/item:visible',
    },
  ];

  redirectToRessourcesPro() {
    const url = location.href;
    const fragments = url.split('/').slice(0, 7);
    fragments.push('ressources-professionnelles');
    window.open(fragments.join('/'), '_blank');
  }

  getInterrelationsFromAptitude(aptitude: Aptitude) {
    const etape = this.etape();
    if (!etape) return [];
    const aptitudeFromCertification = this.aptitudesCertification().find((x) => x.id === aptitude.id);
    const interrelationsFromEtape = [
      ...(etape.interrelations.map((x) => x.idInterrelation) ?? []),
      ...(aptitudeFromCertification?.interrelations.map((x) => x.id) ?? []),
    ];

    return this.#interrelationStore
      .parPhase(aptitude.idPhase)
      .filter(
        (x) =>
          this.interrelations.value.some((y) => y.id.split('-', 2)[1] === x.id) ||
          interrelationsFromEtape.includes(x.id),
      )
      .map((x) => ({
        ...x,
        value: { id: `${aptitude.id}-${x.id}`, nom: x.nom, idInterrelation: x.id, idAptitude: aptitude.id },
      }));
  }

  getProductionsFromAptitude(aptitude: Aptitude) {
    const etape = this.etape();
    const aptitudeFromCertification = this.aptitudesCertification().find((x) => x.id === aptitude.id);
    const productionsFromEtape = [...(etape.productions ?? []), ...(aptitudeFromCertification?.productions ?? [])];
    let productions: Production[] = aptitude.productions ?? [];
    if (etape && productionsFromEtape) {
      productions = aptitude.productions?.filter((x) => productionsFromEtape.some((y) => y.id === x.id)) ?? [];
    }
    return productions;
  }

  get interrelations() {
    return this.form().get(FormControlNames.INTERRELATIONS) as FormControl<{ id: string }[]>;
  }

  get productions() {
    return this.form().get(FormControlNames.PRODUCTIONS) as FormControl<Production[]>;
  }

  get aptitudeForm() {
    return this.form().get(FormControlNames.APTITUDE) as FormControl<Aptitude>;
  }
  get estEvaluee() {
    return this.form().get(FormControlNames.EST_EVALUEE) as FormControl<boolean>;
  }
}
